import type { paths } from "@/generated/openapi-schema";
import * as Sentry from "@sentry/react";
import type { Middleware } from "openapi-fetch";
import createClient from "openapi-fetch";
import { auth } from "./firebase";

export class FetchError extends Error {
  static {
    this.prototype.name = "FetchError";
  }

  detail: unknown;

  constructor(
    message = "",
    { detail, ...rest }: ErrorOptions & { detail?: unknown } = {}
  ) {
    super(message, rest);
    this.detail = detail;
  }

  // FetchError の詳細メッセージを取得する
  // バックエンドで detail に文字列の配列を返している場合はそれを返す
  static getUserErrorMessages(error: Error | null): string[] {
    if (!error) return [];
    if (error instanceof FetchError) {
      if (!Array.isArray(error.detail)) return [];
      if (error.detail.some((item) => typeof item !== "string")) return [];
      return error.detail as string[];
    }
    return [];
  }
}

const errorMiddleware: Middleware = {
  onResponse: async ({ request, response }) => {
    if (!response.ok) {
      const detail = await response
        .json()
        .then((json) => json?.detail ?? null)
        .catch(() => null);
      throw new FetchError(
        `FetchError on ${request.method} ${request.url}. Type is user error: ${response.status} ${response.statusText}`,
        { detail }
      );
    }
  },
  onError({ error, request }) {
    return new FetchError(
      `FetchError on ${request.method} ${request.url}. Type is system error`,
      {
        cause: error,
      }
    );
  },
};

const sentryMiddleware: Middleware = {
  async onResponse({ request, response }) {
    // エラーレスポンスの場合（4xx, 5xx）
    if (!response.ok) {
      try {
        // レスポンスデータを取得（可能であれば）
        const responseData = await response
          .clone()
          .json()
          .catch(() => null);

        Sentry.captureException(
          new Error(`API Error: ${response.status} ${response.statusText}`),
          {
            tags: {
              api_endpoint: request.url,
              method: request.method,
              status_code: response.status,
            },
            extra: {
              request: {
                url: request.url,
                method: request.method,
                headers: Object.fromEntries(request.headers.entries()),
              },
              response: {
                status: response.status,
                statusText: response.statusText,
                data: responseData,
              },
            },
          }
        );
      } catch (error) {
        // インターセプター内でのエラーも捕捉
        Sentry.captureException(error);
      }
    }
    return response;
  },

  // ネットワークエラー（タイムアウトなど）の捕捉
  async onError({ request, error }) {
    Sentry.captureException(error, {
      tags: {
        api_endpoint: request.url,
        method: request.method,
        error_type: "network_error",
      },
      extra: {
        request: {
          url: request.url,
          method: request.method,
          headers: Object.fromEntries(request.headers.entries()),
        },
      },
    });
  },
};

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      request.headers.set("authorization", `Bearer ${token}`);
    }
    request.headers.set("Accept", "application/json");
    return request;
  },
};

export const fetchClient = createClient<paths>({
  baseUrl: import.meta.env["VITE_REACT_APP_OCR_SERVICE_API_URL"],
});

fetchClient.use(authMiddleware);
fetchClient.use(sentryMiddleware);
fetchClient.use(errorMiddleware);
